import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import Tooltip from 'components/common/tool-tip-component';
import { ReactComponent as CloseButton } from 'icons/close-button.svg';
import { ReactComponent as DoubleArrow } from 'icons/double-arrow.svg';
import Button, { BUTTON_TYPES } from 'components/common/button-component';
import { mapSideBySideElementsRequest } from 'store/actions/statement-content-actions';

const CLOSE_ICON_DIMENSION = '28px';
const ICON_SIZE = 25;

export const SideBySideViewFooter = ({
  BLOCK,
  isSwapped,
  numberOfTargetElementSelected,
  numberOfSourceElementSelected,
  onClearSelection,
}) => {
  const dispatch = useDispatch();
  const { statementId, revisionId } = useParams();
  const [shouldDisableMapBtn, setShouldDisableMapBtn] = useState(false);

  useEffect(() => {
    const noElementSelected =
      numberOfTargetElementSelected === 0 &&
      numberOfSourceElementSelected === 0;
    setShouldDisableMapBtn(noElementSelected);

  }, [numberOfTargetElementSelected, numberOfSourceElementSelected]);

  const onMapElements = () => {
    setShouldDisableMapBtn(true);
    dispatch(mapSideBySideElementsRequest(statementId, revisionId));
  };

  return (
    <div className={`${BLOCK}__footer`}>
      <div
        className={classnames(`${BLOCK}__footer-wrap`, {
          [`${BLOCK}__footer-wrap__swap`]: isSwapped,
        })}
      >
        <div
          className={classnames(`${BLOCK}__footer-right`, {
            [`${BLOCK}__footer-right__swap`]: isSwapped,
          })}
        >
          <div
            className={classnames(`${BLOCK}__footer-right-title`, {
              [`${BLOCK}__display-title-modifier`]:
                numberOfSourceElementSelected > 0,
            })}
          >
            {numberOfTargetElementSelected ? (
              <>
                <div className={`${BLOCK}__element-count-label`}>
                  <FormattedMessage
                    id="side-by-side-view.compare.footer.selected-element.count"
                    values={{
                      count: numberOfTargetElementSelected,
                    }}
                  />
                </div>

                <Tooltip
                  id={`${BLOCK}-clearSelection-btn`}
                  position={'top'}
                  text="side-by-side-view.compare.button.cancel.tooltip"
                  key={`${BLOCK}-id__clearSelection-btn`}
                >
                  <CloseButton
                    className={`${BLOCK}__clearSelection-btn`}
                    onClick={onClearSelection}
                    height={CLOSE_ICON_DIMENSION}
                    width={CLOSE_ICON_DIMENSION}
                    role="button"
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <FormattedMessage id="side-by-side-view.compare.footer.right.info" />
              </>
            )}
          </div>
        </div>
        <div
          className={classnames(`${BLOCK}__double-arrow-icon`, {
            [`${BLOCK}__double-arrow-icon__swap`]: isSwapped,
          })}
        >
          <DoubleArrow width={ICON_SIZE} height={ICON_SIZE} />
        </div>
        <div
          className={classnames(`${BLOCK}__footer-left`, {
            [`${BLOCK}__footer-left__swap`]: isSwapped,
          })}
        >
          <div
            className={classnames(`${BLOCK}__footer-left-title`, {
              [`${BLOCK}__display-title-modifier`]:
                numberOfSourceElementSelected > 0,
            })}
          >
            {numberOfSourceElementSelected ? (
              <>
                <div className={`${BLOCK}__element-count-label`}>
                  <FormattedMessage
                    id="side-by-side-view.compare.footer.selected-element.count"
                    values={{
                      count: numberOfSourceElementSelected,
                    }}
                  />
                </div>
                <Tooltip
                  id={`${BLOCK}-clearSelection-btn`}
                  position={'top'}
                  text="side-by-side-view.compare.button.cancel.tooltip"
                  key={`${BLOCK}-id__clearSelection-btn`}
                >
                  <CloseButton
                    className={`${BLOCK}__clearSelection-btn`}
                    onClick={onClearSelection}
                    height={CLOSE_ICON_DIMENSION}
                    width={CLOSE_ICON_DIMENSION}
                    role="button"
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <FormattedMessage id="side-by-side-view.compare.footer.left.info" />
              </>
            )}
          </div>
        </div>
      </div>
      <div className={`${BLOCK}__footer-map-button`}>
        <Button
          id={`${BLOCK}-save`}
          className={`${BLOCK}__save`}
          onClick={onMapElements}
          type={BUTTON_TYPES.primary}
          disabled={
            !numberOfTargetElementSelected ||
            !numberOfSourceElementSelected ||
            !(
              numberOfTargetElementSelected === numberOfSourceElementSelected
            ) ||
            // if side by side feature is disabled, then user should not be able to send map request
            !window?.TIEOUT?.ENV?.FEATURE
              ?.ENABLE_SIDE_BY_SIDE_STATEMENT_ELEMENTS_MAP ||
            // if user clicks on map button to map annotations from source to target statement, it will be disabled until unless response is received
            shouldDisableMapBtn
          }
        >
          <FormattedMessage id="side-by-side-view.compare.button.map" />
        </Button>
      </div>
    </div>
  );
};

SideBySideViewFooter.propTypes = {
  /** Block name for BEM class naming */
  BLOCK: PropTypes.string.isRequired,
  /** Flag to check if source and target statements are swapped */
  isSwapped: PropTypes.bool.isRequired,
  /** Number of selected elements in target statement */
  numberOfTargetElementSelected: PropTypes.number.isRequired,
  /** Number of selected elements in source statement */
  numberOfSourceElementSelected: PropTypes.number.isRequired,
  /** Handler to clear selected elements */
  onClearSelection: PropTypes.func.isRequired,
};

export default SideBySideViewFooter;
